import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Layout from "./Components/Layout";
import LandingPage from "./Pages/LandingPage";
import TreatmentDetails from "./Pages/TreatmentDetailsPage";
import AboutPage from "./Pages/AboutPage";
import UploadPage from "./Pages/UploadPage";
import ManageImages from "./Pages/ManageImagesPage";
import BeforeAndAfter from "./Pages/BeforeAndAfterPage";
import ContactUs from "./Pages/ContactUsPage";
import TreatmentTreeDetails from "./Pages/TreatmentTreeDetailsPage";
import MapComparisonPage from "./Pages/MapComparisonPage";
import NotFoundPage from "./Pages/NotFoundPage";
import AcademyPage from "./Pages/AcademyPage";
import GlobalLoadingIndicator from "./utils/GlobalLoadingIndicator";
import FloatingPhone from "./Components/FloatingPhone";
import "@fortawesome/fontawesome-free/css/all.min.css";

// 🔹 React Query Client (Ensuring proper settings)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity, // Keep data fresh indefinitely to prevent refetching
      gcTime: 60 * 60 * 1000, // Cache data for 1 hour (renamed from cacheTime)
      refetchOnWindowFocus: false, // Prevent refetch when switching tabs
      retry: false, // Disable retries to prevent loops
      refetchOnMount: false, // Use cached data on mount
      refetchOnReconnect: false, // Don't refetch when reconnecting
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      networkMode: "online", // Only fetch when online
      refetchInterval: false, // Disable automatic refetching
      refetchIntervalInBackground: false, // Disable background refetching
    },
  },
});

// Removed PrefetchData component to prevent loading loops

// 🔹 Enhanced Scroll to Top on Page Change
const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    // Immediate scroll to top
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Additional attempts to ensure scroll works
    const scrollToTopImmediate = () => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    // Multiple attempts to ensure scroll works on all browsers
    scrollToTopImmediate();

    setTimeout(scrollToTopImmediate, 10);
    setTimeout(scrollToTopImmediate, 50);
    setTimeout(scrollToTopImmediate, 100);
  }, [location]);

  return null;
};

const App = () => {
  useEffect(() => {
    document.title = "ד״ר שי תלם | רפואת שיניים ואסתטיקה"; // Change this to your desired title
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ScrollToTop />
        {/* <GlobalLoadingIndicator /> */}
        <Layout>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/academy" element={<AcademyPage />} />
            <Route path="/treatment/:name" element={<TreatmentDetails />} />
            <Route path="/upload" element={<UploadPage />} />
            <Route path="/manage-images" element={<ManageImages />} />
            <Route
              path="/before-and-after/:treatmentName"
              element={<BeforeAndAfter />}
            />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/map-comparison" element={<MapComparisonPage />} />
            <Route
              path="/treatments/:treatmentRef"
              element={<TreatmentTreeDetails />}
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Layout>
        <FloatingPhone />
      </Router>
    </QueryClientProvider>
  );
};

export default App;
